/**
 * Shared US market time utilities (Eastern Time).
 * Single source of truth for market hours, ET date/time, and weekday checks.
 * Used by useMarketOpen, Navbar, MarketHolidaysStrip, TickerTape, etc.
 */

import { getHolidayName, getEarlyCloseMinute } from './marketHolidays';

export const MARKET_TZ = 'America/New_York';

/** Market hours in seconds from midnight */
export const MARKET_OPEN_SECS = 9 * 3600 + 30 * 60;   // 9:30 AM
export const MARKET_CLOSE_SECS = 16 * 3600;             // 4:00 PM
export const PRE_MARKET_OPEN_SECS = 4 * 3600;           // 4:00 AM
export const POST_MARKET_CLOSE_SECS = 20 * 3600;        // 8:00 PM

/** Canonical US market session vocabulary — the wire contract
 *  (StockDetail.marketSession). Mirrors api/src/utils/marketTime.ts. */
export type MarketSession = 'pre-market' | 'regular' | 'after-hours' | 'closed';

/** Regular-session close in seconds-from-midnight for an ET date — 4:00 PM,
 *  or 1:00 PM on a listed half-day early close. */
function closeSecsFor(etDate: string): number {
  const earlyMin = getEarlyCloseMinute(etDate);
  return earlyMin != null ? earlyMin * 60 : MARKET_CLOSE_SECS;
}

/** Get current ET time parts */
export function getETTimeParts(now = new Date()): {
  weekday: number;  // 0=Sun, 1=Mon...6=Sat
  hour: number;
  minute: number;
  second: number;
  totalSecs: number;
} {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MARKET_TZ,
    hour: 'numeric', minute: 'numeric', second: 'numeric',
    weekday: 'short', hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parseInt(parts.find((p) => p.type === t)?.value ?? '0', 10);
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayStr = parts.find((p) => p.type === 'weekday')?.value ?? 'Mon';
  let h = get('hour');
  if (h === 24) h = 0;
  const m = get('minute');
  const s = get('second');
  return { weekday: weekdays.indexOf(dayStr), hour: h, minute: m, second: s, totalSecs: h * 3600 + m * 60 + s };
}

/** Get today's date in ET as YYYY-MM-DD */
export function getETDate(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: MARKET_TZ }).format(now);
}

/** Get current year in ET */
export function getETYear(now = new Date()): number {
  return parseInt(new Intl.DateTimeFormat('en-US', { timeZone: MARKET_TZ, year: 'numeric' }).format(now));
}

/** Check if it's a weekday in ET */
export function isETWeekday(now = new Date()): boolean {
  const { weekday } = getETTimeParts(now);
  return weekday >= 1 && weekday <= 5;
}

/** Check if regular market is open (9:30 AM – 4:00 PM ET, weekdays,
 *  excluding full-day holidays). Holiday-aware so every consumer
 *  (useMarketOpen → ticker tape, navbar lock, …) agrees: a weekday
 *  holiday like Memorial Day reads closed, not open. */
export function isMarketOpen(now = new Date()): boolean {
  const { weekday, totalSecs } = getETTimeParts(now);
  if (weekday < 1 || weekday > 5) return false;
  const etDate = getETDate(now);
  if (getHolidayName(etDate)) return false;
  return totalSecs >= MARKET_OPEN_SECS && totalSecs < closeSecsFor(etDate);
}

/** Classify the current ET clock into a market session (wire vocabulary).
 *  Holiday- and half-day-aware. Single canonical client implementation —
 *  marketHours.ts re-exports this. */
export function getMarketSession(now = new Date()): MarketSession {
  const { weekday, totalSecs } = getETTimeParts(now);
  if (weekday < 1 || weekday > 5) return 'closed';
  const etDate = getETDate(now);
  if (getHolidayName(etDate)) return 'closed';
  const closeSecs = closeSecsFor(etDate);
  if (totalSecs >= PRE_MARKET_OPEN_SECS && totalSecs < MARKET_OPEN_SECS) return 'pre-market';
  if (totalSecs >= MARKET_OPEN_SECS && totalSecs < closeSecs) return 'regular';
  if (totalSecs >= closeSecs && totalSecs < POST_MARKET_CLOSE_SECS) return 'after-hours';
  return 'closed';
}

/** True during regular trading hours (holiday- and half-day-aware). */
export function isDuringMarketHours(now = new Date()): boolean {
  return getMarketSession(now) === 'regular';
}

/** Get current market phase. Full-day holidays have no pre/main/post
 *  session, so a listed holiday returns 'closed' regardless of clock. */
export function getMarketPhase(now = new Date()): 'pre' | 'main' | 'post' | 'closed' {
  const { weekday, totalSecs } = getETTimeParts(now);
  if (weekday < 1 || weekday > 5) return 'closed';
  const etDate = getETDate(now);
  if (getHolidayName(etDate)) return 'closed';
  const closeSecs = closeSecsFor(etDate);
  if (totalSecs >= PRE_MARKET_OPEN_SECS && totalSecs < MARKET_OPEN_SECS) return 'pre';
  if (totalSecs >= MARKET_OPEN_SECS && totalSecs < closeSecs) return 'main';
  if (totalSecs >= closeSecs && totalSecs < POST_MARKET_CLOSE_SECS) return 'post';
  return 'closed';
}

/** The next regular-session open (9:30 ET) that is neither a weekend nor a
 *  listed full-day holiday, starting from `now`. Bounded 14-day walk so a
 *  bad calendar can't loop forever. DST transition days (23 h / 25 h) round
 *  to 24 h — an up-to-1-h skew twice a year on this "next open" estimate,
 *  acceptable for a countdown. Returns the seconds remaining plus the ET
 *  weekday/date of that open so callers can label it without re-deriving. */
export function getNextMarketOpen(now = new Date()): { secondsUntil: number; weekday: number; etDate: string } {
  const baseEtDate = getETDate(now);
  for (let hops = 0; hops < 14; hops++) {
    const etDate = addEtDays(baseEtDate, hops);
    const [y, m, d] = etDate.split('-').map((n) => parseInt(n, 10));
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // DST-immune
    if (weekday < 1 || weekday > 5 || getHolidayName(etDate)) continue;
    // Resolve 9:30 ET on this date to a real UTC instant so the countdown is
    // exact across DST (a fixed +86.4M-ms hop is not one ET day on transition
    // days near ET midnight — it would skip/double-count a date).
    const openUtc = etLocalToUtc(etDate, MARKET_OPEN_SECS / 60);
    if (openUtc.getTime() > now.getTime()) {
      return { secondsUntil: Math.round((openUtc.getTime() - now.getTime()) / 1000), weekday, etDate };
    }
  }
  // Defensive — a 14-day window always contains a trading day.
  return { secondsUntil: 86400, weekday: getETTimeParts(now).weekday, etDate: getETDate(now) };
}

/** Seconds from `now` until the next holiday/weekend-aware 9:30 ET open. */
export function secondsUntilNextOpen(now = new Date()): number {
  return getNextMarketOpen(now).secondsUntil;
}

/** Resolve the actual UTC offset (in ms) for the ET wall-clock at this instant.
 *  EST = −5h, EDT = −4h. Uses Intl `longOffset` so DST is handled natively. */
function etOffsetMs(date: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MARKET_TZ,
    timeZoneName: 'longOffset',
  }).formatToParts(date);
  const off = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT-05:00';
  const mt = /GMT([+-])(\d{2}):(\d{2})/.exec(off);
  if (!mt) return -5 * 3600_000;
  const sign = mt[1] === '+' ? 1 : -1;
  return sign * (parseInt(mt[2], 10) * 3600_000 + parseInt(mt[3], 10) * 60_000);
}

/** Convert an ET wall-clock (YYYY-MM-DD + minutes-of-day, 0..1439) to a real
 *  UTC `Date`. Two-pass so DST-transition instants converge. Mirrors
 *  api/src/utils/marketTime.ts:etLocalToUtc. */
export function etLocalToUtc(ymd: string, minutesOfDay = 0): Date {
  const [y, mo, d] = ymd.split('-').map((n) => parseInt(n, 10));
  const naiveUtc = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0) + minutesOfDay * 60_000);
  const off1 = etOffsetMs(naiveUtc);
  const candidate = new Date(naiveUtc.getTime() - off1);
  const off2 = etOffsetMs(candidate);
  if (off2 === off1) return candidate;
  return new Date(naiveUtc.getTime() - off2);
}

/** Shift a YYYY-MM-DD string by N calendar days, preserving ET-date
 *  semantics (UTC-midnight ms-arithmetic is DST-immune). */
export function addEtDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** Hour ET rolls onto the next trading session. Pre-market opens at 04:00, but
 *  the scanner and the intraday caches have nothing for the new day until the
 *  feeds wake up, so treating midnight as the boundary points every "current
 *  session" surface at an empty day for seven hours. */
export const SESSION_ROLL_HOUR_ET = 7;

/** The ET trading date the app should treat as the CURRENT session.
 *
 *  Differs from `getETDate` in two ways, both of which matter to anything that
 *  labels or defaults to "today":
 *   - Rolls at 07:00 ET, not midnight. At 04:35 ET the session you are working
 *     off is still the previous calendar day.
 *   - Walks back over weekends, so Saturday and Sunday resolve to Friday.
 *
 *  Holidays are not handled (the list is server-side) — a holiday morning
 *  resolves to that empty day, the same minor edge the scanner page already
 *  accepted. Bounded loop; worst case is two backward steps. */
export function currentSessionEtYmd(now = new Date()): string {
  const { hour } = getETTimeParts(now);
  let ymd = getETDate(now);
  if (hour < SESSION_ROLL_HOUR_ET) ymd = addEtDays(ymd, -1);
  for (let i = 0; i < 7; i++) {
    const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
    const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun, 6=Sat
    if (wd >= 1 && wd <= 5) break;
    ymd = addEtDays(ymd, -1);
  }
  return ymd;
}
